const PrivacyPolicyForEmployers = () => (
  <div className="flex flex-col gap-2 text-sm font-inter">
    <h2 className="mb-2">Privacy Policy for Companies</h2>
    <div className="px-0 md:px-6">
      <p>
        <strong>Effective Date:</strong> May 2, 2025
      </p>
      <p className="mt-4">
        Qureos Inc. ("Qureos," "we," "our," or "us") respects and protects the
        privacy of companies and their representatives who use our Platform.
        This Privacy Policy explains how we collect, use, store, and protect the
        data related to your company and the individuals who act on its behalf.
        Our mission is to support compliant, secure, and transparent data
        practices while enabling your business to benefit from our recruitment
        technology.
      </p>
      <p className="mt-4">
        By accessing or using Qureos as a Company, you agree to the practices
        described in this policy.
      </p>
    </div>

    <h3 className="mt-8">1. Definitions</h3>
    <div className="px-0 md:px-6">
      <p>
        Understanding key terms helps avoid confusion. In this Privacy Policy:
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>"Company"</strong>: Refers to any legal entity that creates an
          employer account on Qureos to post jobs, manage applicants, and access
          recruitment tools.
        </li>
        <li className="mb-4">
          <strong>"Representative"</strong>: Is any employee, contractor, or
          agent of the Company who accesses Qureos on its behalf.
        </li>
        <li className="mb-4">
          <strong>"Platform"</strong>: Refers to the Qureos website
          (Qureos.com), software tools, and services designed for talent
          sourcing and management.
        </li>
        <li className="mb-4">
          <strong>"Personal Data"</strong>: Includes any information relating to
          an identified or identifiable individual, such as a company
          representative's name or email address.
        </li>
        <li className="mb-4">
          <strong>"Processing"</strong>: Means any operation performed on data,
          such as collection, recording, structuring, storing, using,
          disclosing, or deleting.
        </li>
        <li className="mb-4">
          <strong>"Services"</strong>: Means all the features provided to
          Companies, including job listings, candidate search, messaging,
          analytics, and billing.
        </li>
      </ul>
      <p>
        These definitions form the basis of this policy and apply consistently
        throughout the document.
      </p>
    </div>

    <h3 className="mt-8">2. Scope</h3>
    <div className="px-0 md:px-6">
      <p>
        This policy applies to all Companies and their Representatives using the
        Qureos Platform for recruitment purposes. It covers data collected
        during account creation, job posting, communication with candidates, and
        subscription billing.
      </p>
      <p className="mt-4">
        This Privacy Policy does not apply to Candidates, who are subject to
        their own separate privacy terms. If you are using Qureos as both a
        Candidate and a Representative, both privacy policies may apply to
        different aspects of your use.
      </p>
      <p className="mt-4">
        Understanding this scope helps you know which rules apply to your
        interactions with us.
      </p>
    </div>

    <h3 className="mt-8">3. Information We Collect</h3>
    <div className="px-0 md:px-6">
      <p>
        To provide Companies with our services, we collect information at both
        the organizational and individual level. The categories include:
      </p>

      <h4 className="mt-4">3.1 Company Information</h4>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Company Name</strong>: Required for account registration and
          used in candidate-facing job listings.
        </li>
        <li className="mb-4">
          <strong>Industry and Size</strong>: Helps categorize your company and
          tailor service experiences.
        </li>
        <li className="mb-4">
          <strong>Company Description</strong>: Optional content you provide to
          enhance your employer brand.
        </li>
        <li className="mb-4">
          <strong>Job Posts</strong>: Includes job title, responsibilities,
          location, salary (if provided), and required qualifications.
        </li>
      </ul>
      <p>
        We use this information to represent your company in job listings and
        support candidate-job matching.
      </p>

      <h4 className="mt-4">3.2 Representative Information</h4>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Full Name</strong>: Used for identification, access control,
          and personalization.
        </li>
        <li className="mb-4">
          <strong>Email Address</strong>: Required for account login,
          communications, and billing updates.
        </li>
        <li className="mb-4">
          <strong>Phone Number</strong>: Used for support, identity
          verification, and time-sensitive alerts.
        </li>
        <li className="mb-4">
          <strong>Role or Title</strong>: Clarifies your authority within the
          company account and helps route communications appropriately.
        </li>
      </ul>
      <p>
        This personal data allows us to deliver secure and efficient service to
        the right point of contact within your team.
      </p>

      <h4 className="mt-4">3.3 Payment and Billing Information</h4>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Billing Address</strong>: Required for generating accurate
          invoices.
        </li>
        <li className="mb-4">
          <strong>Payment Method</strong>: Credit card or wire transfer,
          depending on your subscription cycle.
        </li>
        <li className="mb-4">Monthly plans must be paid by card.</li>
        <li className="mb-4">
          Wire transfers are only available for annual plans.
        </li>
      </ul>
      <p>
        We do not store full payment credentials on our servers; payments are
        processed securely through third-party gateways.
      </p>
      <p>
        By collecting only the data we need, we minimize risk and enhance
        security for your team.
      </p>
    </div>

    <h3 className="mt-8">4. How We Collect Information</h3>
    <div className="px-0 md:px-6">
      <p>We collect Company data through several methods:</p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Account Creation</strong>: When you sign up for a company
          account, you submit details about your business and your
          representative(s).
        </li>
        <li className="mb-4">
          <strong>Platform Usage</strong>: We track actions such as job post
          creation, candidate profile views, and subscription changes.
        </li>
        <li className="mb-4">
          <strong>Communications</strong>: Information collected through
          customer support queries or feedback forms.
        </li>
        <li className="mb-4">
          <strong>Third-Party Services</strong>: We may enrich company profiles
          using public business registries or LinkedIn for verification
          purposes.
        </li>
      </ul>
      <p>
        These collection methods ensure we maintain a verified, secure, and
        functional employer ecosystem.
      </p>
    </div>

    <h3 className="mt-8">5. How We Use Your Information</h3>
    <div className="px-0 md:px-6">
      <p>We process your data to provide, manage, and improve our Services:</p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Platform Access and Security</strong>: To create your account,
          authenticate logins, and prevent fraud.
        </li>
        <li className="mb-4">
          <strong>Job Distribution</strong>: To publish jobs and promote them to
          relevant candidate segments.
        </li>
        <li className="mb-4">
          <strong>Candidate Matching</strong>: To recommend candidates based on
          your job criteria.
        </li>
        <li className="mb-4">
          <strong>Billing and Invoicing</strong>: To process subscriptions and
          send account updates.
        </li>
        <li className="mb-4">
          <strong>Support and Communication</strong>: To respond to inquiries
          and deliver service notifications.
        </li>
        <li className="mb-4">
          <strong>Product Improvement</strong>: To analyze usage and improve
          features over time.
        </li>
      </ul>
      <p>
        We only use your data in ways that directly support our contractual and
        service delivery obligations.
      </p>
    </div>

    <h3 className="mt-8">6. Legal Basis for Processing</h3>
    <div className="px-0 md:px-6">
      <p>
        We collect and use your information based on the following lawful
        grounds:
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Contractual Necessity</strong>: To provide the services
          outlined in your subscription or service agreement.
        </li>
        <li className="mb-4">
          <strong>Legitimate Interests</strong>: To maintain platform security,
          analyze trends, and improve service reliability.
        </li>
        <li className="mb-4">
          <strong>Consent</strong>: For optional marketing communications or
          integrations.
        </li>
        <li className="mb-4">
          <strong>Legal Obligation</strong>: To comply with tax laws, anti-fraud
          requirements, or government disclosures.
        </li>
      </ul>
      <p>
        Our processing aligns with global standards such as the GDPR and
        applicable UAE and KSA regulations.
      </p>
    </div>

    <h3 className="mt-8">7. How We Share Your Information</h3>
    <div className="px-0 md:px-6">
      <p>We share Company data selectively and with safeguards:</p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>With Candidates</strong>: When a job is posted, we show
          Company details such as name, logo, and description.
        </li>
        <li className="mb-4">
          <strong>With Service Providers</strong>: We use vendors for cloud
          storage, analytics, payment processing, and platform monitoring. They
          operate under strict contracts.
        </li>
        <li className="mb-4">
          <strong>With Regulators</strong>: If legally required, we may disclose
          information to authorities (e.g., for audit or investigation
          purposes).
        </li>
      </ul>
      <p>
        We never sell, lease, or trade Company data or Representative
        information.
      </p>
    </div>

    <h3 className="mt-8">8. International Data Transfers</h3>
    <div className="px-0 md:px-6">
      <p>To comply with data residency rules and optimize performance:</p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>EU-based Companies</strong>: Data is stored in the European
          Union.
        </li>
        <li className="mb-4">
          <strong>UAE-based Companies</strong>: Data is stored within the UAE.
        </li>
        <li className="mb-4">
          <strong>KSA-based Companies</strong>: Data is stored inside the
          Kingdom of Saudi Arabia.
        </li>
      </ul>
      <p>
        If your data must be transferred outside your region, we use encryption
        and standard contractual clauses to protect it.
      </p>
    </div>

    <h3 className="mt-8">9. Data Security</h3>
    <div className="px-0 md:px-6">
      <p>We apply technical and organizational measures to protect data:</p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Encryption</strong>: All sensitive data is encrypted during
          storage and transmission.
        </li>
        <li className="mb-4">
          <strong>Access Restrictions</strong>: Only trained personnel can
          access company data with need-based permissions.
        </li>
        <li className="mb-4">
          <strong>Monitoring</strong>: Our systems are monitored for anomalies,
          misuse, or breach attempts.
        </li>
        <li className="mb-4">
          <strong>Incident Response</strong>: In the event of a data breach, we
          notify you and regulators as required by law.
        </li>
      </ul>
      <p>
        We continuously evaluate and update our security infrastructure to adapt
        to new threats.
      </p>
    </div>

    <h3 className="mt-8">10. Your Rights</h3>
    <div className="px-0 md:px-6">
      <p>As a Representative, you have the following rights:</p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Access</strong>: You may request a copy of the data we hold
          about you.
        </li>
        <li className="mb-4">
          <strong>Rectification</strong>: You can correct inaccurate or outdated
          details.
        </li>
        <li className="mb-4">
          <strong>Erasure</strong>: You may request that we delete your personal
          data, subject to retention rules.
        </li>
        <li className="mb-4">
          <strong>Restriction</strong>: You can limit how your data is used in
          certain circumstances.
        </li>
        <li className="mb-4">
          <strong>Objection</strong>: You may object to certain uses of your
          personal data.
        </li>
      </ul>
      <p>
        These rights can be exercised by contacting hello@qureos.com or through
        your Platform account.
      </p>
    </div>

    <h3 className="mt-8">11. Data Retention</h3>
    <div className="px-0 md:px-6">
      <p>We only retain your data as long as necessary:</p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Active Accounts</strong>: Data is retained while the
          subscription is active.
        </li>
        <li className="mb-4">
          <strong>Terminated Accounts</strong>: Data is retained for up to 90
          days after termination unless legally required longer.
        </li>
        <li className="mb-4">
          <strong>Financial Records</strong>: Retained for at least 7 years in
          compliance with tax laws.
        </li>
      </ul>
      <p>We securely delete data once the retention period expires.</p>
    </div>

    <h3 className="mt-8">12. Changes to This Policy</h3>
    <div className="px-0 md:px-6">
      <p>
        We may update this policy to reflect service changes, regulatory shifts,
        or internal improvements. When updates are significant, we'll notify you
        via email or through a platform alert before they take effect.
      </p>
      <p>
        We encourage all Company users to review this policy regularly to stay
        informed.
      </p>
    </div>

    <h3 className="mt-8">13. Contact Us</h3>
    <div className="px-0 md:px-6">
      <p>
        If you have questions or need to exercise your rights regarding this
        policy:
      </p>
      <p>
        <strong>Qureos Inc.</strong>
      </p>
      <ul className="list-disc pl-5">
        <li>
          Email:{' '}
          <a href="mailto:hello@qureos.com" className="text-qureosPrimary">
            hello@qureos.com
          </a>
        </li>
        <li>Jurisdiction: UAE and KSA</li>
      </ul>
    </div>
  </div>
)

export default PrivacyPolicyForEmployers
